import { getConfiguredTelemetryTransport } from "@bestowinc/logger/client";

import {
  getPerformanceTelemetryEnvironment,
  getPerformanceTelemetryShellVersion,
} from "./context";
import { shouldSamplePerformanceEvent } from "./sampling";
import type {
  PerformanceMetricAttributes,
  PerformanceMetricName,
} from "./types";

const MAX_ATTRIBUTE_COUNT = 20;

function sanitizeAttributes(
  attributes: PerformanceMetricAttributes,
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) {
      continue;
    }
    if (Object.keys(result).length >= MAX_ATTRIBUTE_COUNT) {
      break;
    }
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      result[key] = value;
    }
  }
  return result;
}

export function recordPerformanceMetric(
  name: PerformanceMetricName,
  attributes: Omit<PerformanceMetricAttributes, "environment" | "shellVersion">,
): void {
  const environment = getPerformanceTelemetryEnvironment();
  const shellVersion = getPerformanceTelemetryShellVersion();

  const sampled = shouldSamplePerformanceEvent(attributes.outcome, environment);
  if (!sampled) {
    return;
  }

  const transport = getConfiguredTelemetryTransport();
  if (!transport?.recordMetric) {
    return;
  }

  const payload = sanitizeAttributes({
    ...attributes,
    environment,
    shellVersion,
    sampled,
  });

  try {
    transport.recordMetric(name, attributes.durationMs, payload);
  } catch {
    // Vendor transport failures must never break shell rendering.
  }
}
